import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Popper,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import parse from 'html-react-parser';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { Link, useParams } from 'react-router-dom';

import {
  confirmationInitial,
  deleteIssue,
  deleteIssueBulk,
  downloadFile,
  getIssueData,
  getIssuePermissions,
} from '../../../packages/common/api';
import Page from '../../../packages/common/shared-ui/src/components/Page';
import WarningModal from '../../../packages/common/shared-ui/src/components/WarningModal';
import ClearFormFieldIcon from '../../../packages/common/shared-ui/src/icons/ClearFormFieldIcon';
import CopyIcon from '../../../packages/common/shared-ui/src/icons/CopyIcon';
import DeleteIssueIconBig from '../../../packages/common/shared-ui/src/icons/DeleteIssueIconBig';
import EditIconBig from '../../../packages/common/shared-ui/src/icons/EditIconBig';
import { useTranslation } from '../../../packages/common/utils/getLocalString';
import { generateRedirectUrl } from '../../../packages/common/utils/getRedirectUrl';
import { store } from '../app/redux';
import TimesheetsCreateEditModal from '../spentTime/TimesheetsCreateEditModal/TimesheetsCreateEditModal';

import IssueConfirmations from './components/IssueConfirmations/IssueConfirmations';
import IssueTabContractStages from './components/IssueContractStages/IssueContractStages';
import IssueFiles from './components/IssueFiles/IssueFiles';
import IssueTabHistory from './components/IssueHistory/IssueHistory';
import IssueRelated from './components/IssueRelated/IssueRelated';
import IssueSubTasks from './components/IssueSubTasks/IssueSubTasks';
import IssueWatchers from './components/IssueWatchers/IssueWatchers';

// Original class: blockWrapper
const StyledBlockWrapper = styled(Box)(theme => ({
  width: '100%',
  padding: '24px 32px',
  borderRadius: '16px',
  backgroundColor: '#fff',
}));
// Original class: issueLink
const StyledIssueLink = styled(Link)(theme => ({
  '&:hover': {
    textDecoration: 'underline',
  },
}));
// Original class: fieldsHolder
const StyledFieldsHolder = styled(Box)(theme => ({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
}));
// Original class: pageText
const StyledPageText = styled(Box)(theme => ({
  '& p': {
    margin: 0,
  },
}));
// Original class: editorField
const StyledEditorField = styled(Grid)(theme => ({
  //color: '#6270d0',
  wordBreak: 'break-word',

  overflow: 'hidden',
  color: '#212346',
  fontSize: '14px',
  lineHeight: '20px',

  '& a': {
    color: '#6270d0',
    '&:hover': {
      textDecoration: 'underline',
      textDecorationColor: '#212346',
    },
  },
  '& img': {
    maxWidth: '100%',
  },
}));
// Original class: tooltipForm
const StyledTooltipForm = styled(Popper)(theme => ({
  '& .MuiTooltip-tooltip': {
    backgroundColor: '#F1F2F8',
    borderRadius: '8px',
    padding: '4px',
    color: '#212346',
    fontSize: '12px',
    lineHeight: '20px',
    fontWeight: 400,
    margin: 0,
    marginBottom: '0!important',
  },
}));

const saveAs = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();

  // Очистка
  link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const IssuePage = () => {
  const getLocalString = useTranslation();
  const IssueAction = {
    COPY: 'copy',
    DELETE: 'delete',
    EDIT: 'edit',
    ADD_TIMESHEET: 'add_timesheet',
  };
  const params = useParams();
  const pageParams = useParams();
  const navigate = useNavigate();
  const redmineToken = useSelector(state => state.user.redmine_access_token);
  const [pageTitle, setPageTitle] = useState('');
  const [issue, setIssue] = useState({});
  const [issueServices, setIssueServices] = useState({});
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blockConfirm, setBlockConfirm] = useState(false);
  const [allFields, setAllFields] = useState([]);
  const [descriptionField, setDescriptionField] = useState(null);
  const [finalDescriptionField, setFinalDescriptionField] = useState(null);
  const [files, setFiles] = useState([]);
  const [showDeleteIssueWarning, setShowDeleteIssueWarning] = useState(false);
  const [containerHeight, setContainerHeight] = useState('');
  const state = store.getState();
  const elRefs = useRef([]);
  const [openCreateEditModal, setCreateEditModal] = useState(false);
  const [openWarningModal, setOpenWarningModal] = useState(false);

  const handleIssueError = useCallback(
    error => {
      if (error) {
        navigate(`/errors/error-${error[0].status}`);
      }
    },
    [navigate],
  );

  useEffect(() => {
    let ignore = false;
    const getIssue = getIssueData(params.id);
    const getPermissions = getIssuePermissions(params.id);
    function handleResults(results) {
      const errors = results
        .filter(result => result.status === 'rejected')
        .map(result => result.reason);
      if (errors.length) {
        const error = errors.map(item => item.response);
        handleIssueError(error);
      }
      if (!ignore) {
        return results.map(
          result => result.status !== 'rejected' && result.value.data,
        );
      }
    }
    Promise.allSettled([getIssue, getPermissions]).then(data => {
      const [issue, permission] = handleResults(data);
      if (issue) {
        setIssue(issue.issue);
        setIssueServices(issue.services);
        const formattedCustoms = issue.issue.customs.map(item => ({
          ...item,
          custom: true,
        }));
        setAllFields(
          issue.issue.standards
            .filter(item => item.fieldId !== 'description')
            .concat(formattedCustoms),
        );
        setPageTitle(`${issue.issue.subject} #${issue.issue.issueId}`);
      }
      setDescriptionField(
        issue.issue.standards.find(item => item.fieldId === 'description'),
      );
      permission && setPermissions(permission);
      !issue.services.isConfirmIssue && setLoading(false);
    });
    return () => {
      ignore = true;
    };
  }, [handleIssueError, params.id]);

  useEffect(() => {
    if (descriptionField) {
      if (files.length > 0) {
        const imgs = descriptionField?.valueName.split('!');
        imgs.reduce((acc, string, index) => {
          let finalValue = string;
          const value = files.find(
            item => string.includes('.') && string === item.filename,
          );
          if (value) {
            finalValue = `<img src=${value.attachmentUrl} alt=${value.filename} />`;
          }
          imgs[index] = finalValue;
          return acc;
        }, []);
        setFinalDescriptionField(imgs);
      } else {
        setFinalDescriptionField(descriptionField?.valueName);
      }
    }
  }, [descriptionField, files]);

  useEffect(() => {
    if (issueServices.isConfirmIssue) {
      const url = generateRedirectUrl({
        host: window.location.origin,
        path: `/issues/${issue.issueId}`,
        redmineToken,
        isWithAuth: false,
        skipRedirection: true,
      });
      window.open(url, '_self');
    }
  }, [issue.issueId, issueServices, redmineToken]);

  useEffect(() => {
    if (allFields.length > 0) {
      const items = document.getElementsByClassName('pageLine');
      const allFieldsHeight = [...items].reduce((acc, rec) => {
        const style = getComputedStyle(rec);
        const height =
          rec.offsetHeight + Number(style.marginTop.replace('px', ''));
        return acc + height;
      }, 0);
      allFieldsHeight > 0 &&
        setContainerHeight(`${(allFieldsHeight + 200) / 2}px`);
    }
  }, [allFields.length]);

  const handleDeleteIssue = useCallback(
    async (force = false) => {
      const issueLink = pageParams.projectId
        ? `/projects/${pageParams.projectId}/issues`
        : `/issues`;
      let params = {};
      force && (params.force = true);
      try {
        const response = await deleteIssue(issue.issueId, params);
        if (response) {
          setOpenWarningModal(false);
          navigate(issueLink);
        }
      } catch (error) {
        error.response.status === 420 && setShowDeleteIssueWarning(true);
        console.error('ERROR WITH DELETE', error);
      }
    },
    [issue.issueId, navigate, pageParams.projectId],
  );

  const handleIssueAction = useCallback(
    action => {
      let url = '';
      switch (action) {
        case IssueAction.EDIT:
          url = pageParams.projectId
            ? `/projects/${pageParams.projectId}/issues/${params.id}/edit`
            : `/issues/${params.id}/edit`;
          window.open(url, '_self');
          break;
        case IssueAction.COPY:
          url = pageParams.projectId
            ? `/projects/${pageParams.projectId}/issues/${params.id}/copy?trackerId=${issue.trackerId}&projectId=${issue.projectId}`
            : `/issues/${params.id}/copy?trackerId=${issue.trackerId}&projectId=${issue.projectId}`;
          window.open(url, '_blank');
          break;
        case IssueAction.ADD_TIMESHEET:
          setCreateEditModal(true);
          break;
        case IssueAction.DELETE:
          setOpenWarningModal(true);
          break;
      }
    },
    [
      IssueAction.EDIT,
      IssueAction.COPY,
      IssueAction.ADD_TIMESHEET,
      IssueAction.DELETE,
      pageParams.projectId,
      params.id,
      issue.trackerId,
      issue.projectId,
    ],
  );
  const handleModalAction = useCallback(
    async e => {
      switch (e.currentTarget.attributes.value.value) {
        case 'save':
          await handleDeleteIssue(true);
          break;
        case 'delete':
          setOpenWarningModal(false);
          setShowDeleteIssueWarning(false);
          break;
      }
    },
    [handleDeleteIssue],
  );
  const handleDownloadFile = useCallback(async (fileId, fileName) => {
    const response = await downloadFile(fileId, fileName);

    saveAs(response.data, fileName);
  }, []);
  const handleConfirmIssue = useCallback(
    async id => {
      setBlockConfirm(true);
      try {
        let rows = [];
        id && rows.push(id);
        const { data: response } = await confirmationInitial(rows);
        setBlockConfirm(false);
        if (response) {
          navigate(`/confirmations/${Number(response)}`);
        }
      } catch (error) {
        console.error('ERROR WITH POST CONFIRMATION INITIAL', error);
        setBlockConfirm(false);
      }
    },
    [navigate],
  );
  const handleReinitConfirmation = useCallback(
    id => {
      let issuesIds = [];
      id && issuesIds.push(Number(id));
      navigate(`/confirmation/reinit?issues=${issuesIds}`);
    },
    [navigate],
  );

  const handleCreateEditModalClose = useCallback(() => {
    setCreateEditModal(false);
  }, []);
  const handleCreateEditModalSave = useCallback(() => {
    setCreateEditModal(false);
  }, []);

  return (
    <>
      {loading ? (
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '70px',
          }}
        >
          <CircularProgress color='secondary' />
        </Grid>
      ) : (
        <Page title={pageTitle} width='100%' sx={{ marginBottom: '34px' }}>
          <TimesheetsCreateEditModal
            open={openCreateEditModal}
            state={'create'}
            handleClose={handleCreateEditModalClose}
            handleSave={handleCreateEditModalSave}
            project={issue.projectId ? issue.projectId : null}
          ></TimesheetsCreateEditModal>
          <WarningModal
            issue
            simple
            isWarningModalOpen={showDeleteIssueWarning}
            handleToggle={handleModalAction}
            closeWarningModal={() => setShowDeleteIssueWarning(false)}
            title={getLocalString('messages', 'deleteIssue')}
          />
          <WarningModal
            issue
            simple
            isWarningModalOpen={openWarningModal}
            handleToggle={handleModalAction}
            closeWarningModal={() => setOpenWarningModal(false)}
            title={getLocalString('messages', 'confirmDelete')}
          />
          <Grid
            container
            sx={{
              justifyContent: 'space-between',
              marginBottom: '20px',
              alignItems: 'center',
            }}
          >
            <Grid>
              <Typography
                variant='h1'
                sx={{
                  padding: '6px 0',
                  color: '#082253',
                }}
              >
                {`${issue.trackerName} #${issue.issueId}`}
              </Typography>
            </Grid>
            <Grid>
              <Grid
                container
                sx={{
                  alignItems: 'center',
                }}
              >
                {permissions.canInitConfirmation && (
                  <Button
                    disabled={blockConfirm}
                    variant='defaultBlueSecondary'
                    disableRipple
                    onClick={() => handleConfirmIssue(issue.issueId)}
                  >
                    <Typography
                      variant='h4'
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {getLocalString('IssuePage', 'buttons', 'init')}
                    </Typography>
                  </Button>
                )}
                {permissions.canReinitConfirmation && (
                  <Button
                    variant='defaultBlueSecondary'
                    disableRipple
                    onClick={() => handleReinitConfirmation(issue.issueId)}
                  >
                    <Typography
                      variant='h4'
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {getLocalString('IssuePage', 'buttons', 'reinit')}
                    </Typography>
                  </Button>
                )}
                {permissions.canLogTime && (
                  <Button
                    variant='defaultGreyPrimary'
                    disableRipple
                    onClick={() => handleIssueAction(IssueAction.ADD_TIMESHEET)}
                  >
                    <Typography
                      variant='h4'
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {getLocalString('buttons', 'addSpentTime')}
                    </Typography>
                  </Button>
                )}
                {permissions.canCopy && (
                  <Tooltip
                    placement='bottom-start'
                    title={getLocalString('labels', 'copy')}
                    slots={{
                      popper: StyledTooltipForm,
                    }}
                  >
                    <IconButton
                      sx={{ padding: '4px', marginLeft: '10px' }}
                      onClick={() => handleIssueAction(IssueAction.COPY)}
                    >
                      <CopyIcon width={24} height={24} viewBox='0 0 24 24' />
                    </IconButton>
                  </Tooltip>
                )}
                {permissions.canEdit && (
                  <Tooltip
                    placement='bottom-start'
                    title={getLocalString('labels', 'edit')}
                    slots={{
                      popper: StyledTooltipForm,
                    }}
                  >
                    <IconButton
                      sx={{ padding: '4px', marginLeft: '10px' }}
                      onClick={() => handleIssueAction(IssueAction.EDIT)}
                    >
                      <EditIconBig />
                    </IconButton>
                  </Tooltip>
                )}
                {permissions.canDelete && (
                  <Tooltip
                    placement='bottom-start'
                    title={getLocalString('labels', 'delete')}
                    slots={{
                      popper: StyledTooltipForm,
                    }}
                  >
                    <IconButton
                      sx={{ padding: '4px', marginLeft: '10px' }}
                      onClick={() => handleIssueAction(IssueAction.DELETE)}
                    >
                      <DeleteIssueIconBig />
                    </IconButton>
                  </Tooltip>
                )}
              </Grid>
            </Grid>
          </Grid>

          <StyledBlockWrapper
            sx={{
              padding: '24px 16px',
            }}
          >
            {issue.hierarchy.map((item, index) => {
              let issueLink = pageParams.projectId
                ? `/projects/${pageParams.projectId}/issues/${item.issueId}`
                : `/issues/${item.issueId}`;
              item.level === '0' &&
                (issueLink = `/projects/${item?.trackerId}`);
              return (
                <Box
                  key={index}
                  sx={{
                    marginLeft: item.level > 1 ? `${item.level * 8}px` : 0,
                    marginTop:
                      item.level === '0'
                        ? 0
                        : item.level === '1'
                          ? '16px'
                          : '8px',
                  }}
                >
                  <Typography
                    variant='h3'
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {issue.issueId.toString() === item.issueId ? (
                      <Box
                        sx={{
                          color: '#082253',
                        }}
                      >
                        {item.subject}
                      </Box>
                    ) : (
                      <Box>
                        <StyledIssueLink
                          to={issueLink}
                          style={{ color: '#082253' }}
                        >
                          <Box component={'span'}>
                            {item.level === '0'
                              ? `${item.trackerName}`
                              : `${item.trackerName} #${item?.issueId}: `}
                          </Box>
                        </StyledIssueLink>
                        <Box
                          component={'span'}
                          sx={{
                            color: '#656985',
                          }}
                        >
                          {item.subject}
                        </Box>
                      </Box>
                    )}
                  </Typography>
                </Box>
              );
            })}
            <Box
              sx={{
                marginTop: '4px',
              }}
            >
              <Typography
                variant='h4'
                sx={{
                  color: '#212346',
                }}
              >
                {`${getLocalString('labels', 'addedBy')} `}
                {issue.authorName}
                {` ${dayjs(issue.created).format('DD.MM.YYYY H:mm')}`}
                {` ${getLocalString('labels', 'updated')}`}
                {` ${dayjs(issue.updated).format('DD.MM.YYYY H:mm')}.`}
              </Typography>
            </Box>
            <StyledFieldsHolder
              sx={{
                marginTop: '32px',
                padding: '0 16px',
                position: 'relative',
                maxHeight: containerHeight,
              }}
            >
              {allFields &&
                allFields.length > 0 &&
                allFields.map((control, index) => (
                  <Grid
                    key={index}
                    ref={ref => {
                      elRefs.current[index] = ref;
                    }}
                    container
                    className={'pageLine'}
                    sx={{
                      marginTop: '4px',
                      maxWidth: '50%',
                    }}
                  >
                    <Grid
                      sx={{
                        width: '200px',
                      }}
                    >
                      <Typography
                        variant='h4'
                        sx={{
                          color: '#41424E',
                          fontWeight: 700,
                        }}
                      >
                        {`${control.fieldName}:`}
                      </Typography>
                    </Grid>
                    <Grid>
                      <Typography variant='h4'>
                        {control.fieldFormat === 'issue_timesheets' ? (
                          <StyledIssueLink
                            to={
                              pageParams.projectId
                                ? `/projects/${
                                    pageParams.projectId
                                  }/timesheets?filters=timesheet.issueId;eq;${control.valueId.join('||')}`
                                : `/timesheets?filters=timesheet.issueId;eq;${control.valueId.join('||')}`
                            }
                          >
                            {control.valueName}
                          </StyledIssueLink>
                        ) : control.fieldFormat === 'attachment' ? (
                          <StyledIssueLink
                            sx={{ cursor: 'pointer' }}
                            onClick={() =>
                              handleDownloadFile(
                                control.valueId,
                                control.valueName,
                              )
                            }
                            component={'span'}
                          >
                            {control.valueName}
                          </StyledIssueLink>
                        ) : control.fieldFormat === 'link' ? (
                          <StyledIssueLink
                            target={'_blank'}
                            to={control.valueName}
                          >
                            <Box sx={{ wordBreak: 'break-word' }}>
                              {control.valueName}
                            </Box>
                          </StyledIssueLink>
                        ) : control.fieldFormat === 'text' ? (
                          <StyledPageText
                            component={'span'}
                            sx={{
                              color: '#212346',
                              wordBreak: 'break-word',
                            }}
                          >
                            {parse(control.valueName)}
                          </StyledPageText>
                        ) : (
                          <Box
                            component={'span'}
                            sx={{
                              color: '#212346',
                              wordBreak: 'break-word',
                            }}
                          >
                            {control.valueName}
                          </Box>
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
            </StyledFieldsHolder>
            {descriptionField && finalDescriptionField && (
              <Box
                sx={{
                  marginTop: '32px',
                  padding: '0 16px',
                }}
              >
                <Grid
                  container
                  sx={{
                    flexDirection: 'column',
                  }}
                >
                  <Grid
                    sx={{
                      width: '200px',
                    }}
                  >
                    <Typography
                      variant='h4'
                      sx={{
                        color: '#41424E',
                        fontWeight: 700,
                      }}
                    >
                      {`${descriptionField.fieldName}:`}
                    </Typography>
                  </Grid>
                  <StyledEditorField
                    item
                    //whiteSpace={'pre-line'}
                    sx={{
                      marginTop: '10px',
                    }}
                  >
                    {Array.isArray(finalDescriptionField)
                      ? finalDescriptionField.map((string, index) => {
                          return (
                            <Box key={index} component={'span'}>
                              {parse(string)}
                            </Box>
                          );
                        })
                      : parse(finalDescriptionField)}
                  </StyledEditorField>
                </Grid>
              </Box>
            )}
            <Grid
              container
              direction={'column'}
              sx={{
                justifyContent: 'flex-start',
                padding: '0 16px',
              }}
            >
              {permissions.canViewWatchers && (
                <Grid
                  sx={{
                    margin: '32px 0 0',
                  }}
                >
                  <IssueWatchers permissions={permissions}></IssueWatchers>
                </Grid>
              )}
              <Grid
                sx={{
                  margin: '32px 0 0',
                }}
              >
                <IssueSubTasks
                  projectId={issue.projectId}
                  trackerId={issue.trackerId}
                  permissions={permissions}
                ></IssueSubTasks>
              </Grid>
              <Grid
                sx={{
                  margin: '32px 0 0',
                }}
              >
                <IssueRelated permissions={permissions}></IssueRelated>
              </Grid>
              {permissions.canViewConfirmation && (
                <Grid
                  sx={{
                    margin: '48px 0 0',
                  }}
                >
                  <IssueConfirmations></IssueConfirmations>
                </Grid>
              )}
              <Grid
                sx={{
                  margin: '32px 0 0',
                }}
              >
                <IssueFiles
                  permissions={permissions}
                  obtainFiles={data => setFiles(data)}
                ></IssueFiles>
              </Grid>
              {issueServices.hasContractStages &&
                permissions.canManageContractStages && (
                  <Grid
                    sx={{
                      margin: '32px 0 0',
                      width: '100%',
                    }}
                  >
                    <IssueTabContractStages></IssueTabContractStages>
                  </Grid>
                )}
              <Grid
                sx={{
                  margin: '32px 0 0',
                }}
              >
                <IssueTabHistory></IssueTabHistory>
              </Grid>
            </Grid>
          </StyledBlockWrapper>
        </Page>
      )}
    </>
  );
};
export default IssuePage;
